import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  EmptyState,
  Notice,
  SearchBox,
  Spinner,
  Table,
  TableWrap,
  Toolbar,
} from 'iboon-ui';

import { api } from '../api';

/**
 * Everything anybody has done, most recent first.
 *
 * The register already wrote this on every change; nothing showed it. An audit
 * trail nobody can read is a table that costs writes and answers no questions
 * -- and "who retired that laptop, and when" is exactly the sort of thing that
 * gets asked months later, by which time nobody remembers.
 *
 * Deliberately not filtered by actor: this is short enough to read, and a
 * filter invites the habit of looking only at somebody in particular.
 */
type Entry = {
  id: string;
  at: string;
  actorEmail: string;
  action: string;
  subject: string | null;
  detail: Record<string, unknown>;
};

/** Plain English for each action, so the page is readable without a key. */
const SAID: Record<string, string> = {
  'asset.created': 'added an asset',
  'asset.updated': 'changed an asset',
  'asset.assigned': 'gave an asset to somebody',
  'asset.returned': 'took an asset back',
  'asset.transferred': 'handed an asset on',
  'asset.state_changed': 'changed what state an asset is in',
  'stock.created': 'added a kind of stock',
  'stock.movement': 'recorded a stock movement',
  'category.created': 'added a category',
  'category.updated': 'changed a category',
  'category.deleted': 'deleted a category',
  'location.created': 'added a place',
  'location.updated': 'changed a place',
  'location.deleted': 'deleted a place',
  'people.synced': 'refreshed the directory from identity',
  'device.claimed': 'registered a machine',
  'device.seen': 'checked a machine in',
  'device.deregistered': 'released a machine',
  'device.linked': 'tied a machine to an asset',
};

const when = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });

/** The one or two things about an entry worth reading at a glance. */
function summarise(entry: Entry): string {
  const detail = entry.detail ?? {};
  const parts = Object.entries(detail)
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    // Ids are in the row already and read as noise here; the words are the point.
    .filter(([key]) => !/id$/i.test(key))
    .map(([key, value]) =>
      Array.isArray(value) ? `${key}: ${value.join(', ')}` : `${key}: ${String(value)}`,
    );
  return parts.join(' · ');
}

export function Activity() {
  const [entries, setEntries] = useState<Entry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(50);

  const load = useCallback(async (howMany: number) => {
    const result = await api.get<{ entries: Entry[] }>(`/api/audit?limit=${howMany}`);
    setEntries(result.entries);
  }, []);

  useEffect(() => {
    load(limit).catch((caught: unknown) =>
      setError(caught instanceof Error ? caught.message : 'Could not load the trail.'),
    );
  }, [limit, load]);

  if (error) {
    return (
      <Notice tone="danger" title="Could not load the trail">
        {error}
      </Notice>
    );
  }

  // Filtered here rather than on the server: the whole list is at most 200
  // rows, and a round trip per keystroke buys nothing at that size.
  const shown = (entries ?? []).filter((entry) => {
    if (!query.trim()) return true;
    const haystack = [entry.actorEmail, entry.action, SAID[entry.action] ?? '', summarise(entry)]
      .join(' ')
      .toLowerCase();
    return haystack.includes(query.trim().toLowerCase());
  });

  return (
    <>
      <Toolbar>
        <SearchBox value={query} onValueChange={setQuery} placeholder="Who, or what happened…" />
        {limit < 200 && (
          <Button variant="ghost" onClick={() => setLimit(200)}>
            Show more
          </Button>
        )}
      </Toolbar>

      {!entries ? (
        <Spinner />
      ) : shown.length === 0 ? (
        <EmptyState
          title={query ? 'Nothing matches' : 'Nothing has happened yet'}
          description={
            query ? 'No entry matches that.' : 'Every change to the register will appear here.'
          }
        />
      ) : (
        <Card padded={false}>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>What</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((entry) => (
                  <tr key={entry.id}>
                    <td className="muted">{when(entry.at)}</td>
                    <td>{entry.actorEmail || <span className="muted">—</span>}</td>
                    <td className="tcell-name">
                      {SAID[entry.action] ?? entry.action}
                      {/* The raw action too, for anything not in the list above
                          and for anybody grepping the database afterwards. */}
                      {SAID[entry.action] && <div className="sub">{entry.action}</div>}
                    </td>
                    <td className="sub">{summarise(entry) || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </Card>
      )}
    </>
  );
}
